import { FavoriteActionButton } from '@app/components/FavoriteActionButton/FavoriteActionButton';
import { ProductCard } from '@app/components/ProductCard';
import { GetWorksResponseShape } from '@app/data/clients/monolithClients/explore/types';
import { useFavoriteStatuses } from '@app/hooks/useFavoriteStatuses';
import {
  Box,
  Button,
  Carousel,
  constants,
  Slide,
  Text,
  TextLink,
} from '@redbubble/design-system';
import { useViewportWidth } from '@redbubble/next/hooks';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { AppProps, DeviceType, UserInfo } from '@redbubble/next/types';
import {
  type Item as AnalyticsEcommerceItem,
  AnalyticsItemsList,
  AnalyticsItemsListIDNameMap,
} from '@redbubble/next-analytics/gtm';
import { usePageProps, useUserInfo } from '@redbubble/next-provider';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  parseHomePageRecommendedWorksToAnalyticsEcommerceItem,
  trackGA4ViewItemListHomePage,
} from '../../analytics';
import { getUrlWithLocale } from '../../utils/getUrlWithLocale';
import styles from './RecommendedWorksFeed.module.scss';

export interface RecommendedWorksFeedProps {
  recommendedWorks: GetWorksResponseShape;
}

const messages = defineMessages({
  title: { defaultMessage: 'Explore designs picked for you' },
  seeMore: { defaultMessage: 'See more' },
  byArtist: {
    defaultMessage: 'By {artistName}',
  },
  productCount: {
    defaultMessage: '{count} products',
    description: 'Number of products for work items in recently viewed.',
  },
});

export const RecommendedWorksFeed: React.FC<RecommendedWorksFeedProps> = ({
  recommendedWorks,
}) => {
  const [{ locale }] = useUserInfo<UserInfo>();
  const { ref, inView } = useInView({ triggerOnce: true });
  const intl = useIntl();
  const { deviceType } = usePageProps<AppProps>();
  const { isOrAbove } = useViewportWidth(deviceType);
  const isDesktop = isOrAbove(DeviceType.Desktop);

  const seeAllPath =
    locale &&
    getUrlWithLocale(locale, '/explore?ref=home-explore-carousel-all');

  useEffect(() => {
    if (inView) {
      const analyticsItems = (() => {
        if (recommendedWorks) {
          return recommendedWorks
            .map((workItem, index) => {
              if (!workItem) return null;
              return parseHomePageRecommendedWorksToAnalyticsEcommerceItem({
                recommendedWork: workItem,
                index,
              });
            })
            .filter(
              (
                item: AnalyticsEcommerceItem | null
              ): item is NonNullable<AnalyticsEcommerceItem> => !!item
            );
        }
        return [];
      })();

      if (analyticsItems && analyticsItems.length > 0) {
        trackGA4ViewItemListHomePage({
          items: analyticsItems,
          listID: AnalyticsItemsList.RecommendedWorksCarousel,
          listName: AnalyticsItemsListIDNameMap.recommended_works_carousel,
        });
      }
    }
  }, [inView, recommendedWorks]);

  const { favoriteStatuses, refresh: refreshFavoriteStatuses } =
    useFavoriteStatuses();

  useEffect(() => {
    refreshFavoriteStatuses(recommendedWorks);
  }, [refreshFavoriteStatuses, recommendedWorks]);

  const slides = recommendedWorks.map((rec, index) => {
    const artistName = rec.artist.shopDisplayName;
    const caption = intl.formatMessage(messages.byArtist, {
      artistName,
    });
    return (
      <Slide key={rec.id}>
        <ProductCard
          key={rec.id}
          title={rec.title}
          workTitle={rec.title}
          link={rec.url}
          artistName={artistName}
          caption={caption}
          imageSrc={rec.image.url}
          matureContent={rec.matureContent}
          analyticsIndex={index}
          analyticsComponentID={AnalyticsItemsList.RecommendedWorksCarousel}
          primaryAction={
            <FavoriteActionButton
              work={rec}
              favoriteStatuses={favoriteStatuses}
              index={index}
            />
          }
          testId={`rwf-media-card-${rec.id}`}
        >
          <Text type="body2">
            <strong>
              {intl.formatMessage(messages.productCount, {
                count: rec.availableProductCount,
              })}
            </strong>
          </Text>
        </ProductCard>
      </Slide>
    );
  });

  return (
    <Box ref={ref}>
      <div className={styles.carouselTitle}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Box>
            <Text block type="display2" element="h2">
              {intl.formatMessage(messages.title)}
            </Text>
          </Box>
        </Box>
        <div className={styles.seeAllDesktop}>
          <TextLink intent={constants.PRIMARY} href={seeAllPath}>
            {intl.formatMessage(messages.seeMore)}
          </TextLink>
        </div>
      </div>
      <Carousel
        profile={isDesktop ? constants.DESKTOP : constants.MOBILE}
        slidesPerView={isDesktop ? 5 : 2}
        hideControls
      >
        {slides}
      </Carousel>
      <div className={styles.seeAllMobile}>
        <Button strong id="SeeAllMobile" href={seeAllPath}>
          {intl.formatMessage(messages.seeMore)}
        </Button>
      </div>
    </Box>
  );
};
